/**
 * B19 — Block-insertion lookup menu (attribute picker).
 *
 * AutoCAD parity: when a dynamic block has a Lookup parameter exposed as
 * a "lookup chevron" grip, clicking the grip opens a small dropdown
 * popup pinned at the cursor that lists the table's authored values
 * (`LookupTable.rows`). Picking a row writes the row's
 * `outputColumn`-bound value (or the first input value if the table has
 * no output column) back to the block-insert entity's per-instance
 * customProperties map, and AutoCAD cascades the parameter graph by
 * matching every other input column → row.
 *
 * Wiring (see App.tsx for the host side):
 *
 *   1. `<GripsDomLayer onLookupGripClick={...}>` invokes the callback
 *      with `{ entityId, parameterId, screen }`.
 *   2. App.tsx stores `{ entityId, parameterId, screen }` in local
 *      state — guards mounting of `<BlockLookupMenu>`.
 *   3. The menu reads the block's `LookupTable` via
 *      `geometryDocument.blocks[blockName].dynamicMetadata.lookupTables`,
 *      filtered by `parameterId` → `LookupParameter.lookupTableId`.
 *   4. User picks a row → `onPick(rowId, label)` → reducer updates the
 *      INSERT's `Param: <name>` property.
 *
 * Pure presentation. No reducer / store access. Replaces the legacy
 * `LookupMenuState` (PR-K removed it during the SVG-renderer purge).
 */

import * as React from "react";

import type { LookupTable } from "@dxf-ai-editor/drawing-model";

import { useT } from "../../i18n";

export interface BlockLookupMenuProps {
  /**
   * Screen-space anchor (CSS pixels relative to the document, i.e.
   * `event.clientX/Y`). The menu uses fixed positioning so it floats
   * above any ancestor with `overflow: hidden`.
   */
  readonly screen: { readonly x: number; readonly y: number };
  /** Display label for the lookup parameter (e.g. "Профиль"). */
  readonly parameterName: string;
  /**
   * The lookup table feeding the dropdown. The component lists each
   * row, using `outputValue` as the visible label (with a fallback to
   * `row.id`). Empty rows render the "no values" empty state.
   */
  readonly table: LookupTable;
  /**
   * Currently active row id (highlighted in the list). Optional —
   * absent = no highlight.
   */
  readonly currentRowId?: string;
  /**
   * Invoked when the user picks a row. Receives the row id and a
   * pre-resolved display label (mirrors the row's outputValue, or the
   * row id if no output column).
   */
  readonly onPick: (rowId: string, label: string) => void;
  /** Invoked on Esc / outside-click / Cancel button. */
  readonly onCancel: () => void;
}

/**
 * Resolve the visible label for a single lookup row. Mirrors what the
 * Properties palette dropdown shows for a Lookup parameter: the row's
 * `outputValue` (string-converted) when present, falling back to a
 * compact representation of the row's input values.
 */
export function resolveRowLabel(
  table: LookupTable,
  row: LookupTable["rows"][number],
): string {
  if (row.outputValue !== undefined) return String(row.outputValue);
  // No output column — show the input values joined with `/` so the
  // user has *something* to pick by.
  const parts: string[] = [];
  for (const col of table.inputColumns) {
    const value = row.inputValues[col.id];
    if (value !== undefined) parts.push(String(value));
  }
  return parts.length > 0 ? parts.join(" / ") : row.id;
}

export function BlockLookupMenu(
  props: BlockLookupMenuProps,
): React.ReactElement {
  const t = useT();
  const { screen, parameterName, table, currentRowId, onPick, onCancel } = props;

  // Esc key + outside-click close handlers. The outer overlay div
  // captures clicks that fall outside the menu and forwards them to
  // onCancel.
  React.useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel]);

  // Position the popup at the click site. We clamp to the viewport
  // (right + bottom edges) so the popup doesn't fall off the screen
  // for clicks near the bottom-right corner. The fallback width is a
  // safe estimate; real layout uses CSS `min-width` instead.
  const PIN_OFFSET = 4;
  const menuWidth = 220;
  const menuMaxHeight = 320;
  const viewportWidth =
    typeof window !== "undefined" ? window.innerWidth : 1024;
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight : 768;
  let left = screen.x + PIN_OFFSET;
  let top = screen.y + PIN_OFFSET;
  if (left + menuWidth > viewportWidth) left = viewportWidth - menuWidth - 4;
  if (top + menuMaxHeight > viewportHeight)
    top = Math.max(viewportHeight - menuMaxHeight - 4, 4);

  const rows = table.rows;
  const empty = rows.length === 0;

  return (
    <div
      // Fullscreen transparent overlay — outside-click cancels.
      data-testid="block-lookup-menu-overlay"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        pointerEvents: "auto",
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
      onContextMenu={(event) => {
        event.preventDefault();
        onCancel();
      }}
    >
      <div
        data-testid="block-lookup-menu"
        role="menu"
        style={{
          position: "absolute",
          left,
          top,
          minWidth: menuWidth,
          maxHeight: menuMaxHeight,
          overflowY: "auto",
          background: "#262629",
          border: "1px solid #3a3a3e",
          borderRadius: 4,
          boxShadow: "0 6px 18px rgba(0, 0, 0, 0.45)",
          padding: "4px 0",
          fontFamily: "Inter, sans-serif",
          fontSize: 12,
          color: "#f0f0f1",
        }}
      >
        <div
          data-testid="block-lookup-menu-title"
          style={{
            padding: "4px 12px",
            fontWeight: 600,
            color: "#f1c40f",
            borderBottom: "1px solid #3a3a3e",
          }}
        >
          {t("block.lookup.menu.title", { name: parameterName })}
        </div>
        {empty ? (
          <div
            data-testid="block-lookup-menu-empty"
            style={{ padding: "8px 12px", opacity: 0.7 }}
          >
            {t("block.lookup.menu.empty")}
          </div>
        ) : (
          rows.map((row) => {
            const label = resolveRowLabel(table, row);
            const isCurrent = row.id === currentRowId;
            return (
              <button
                key={row.id}
                type="button"
                role="menuitem"
                data-testid={`block-lookup-row-${row.id}`}
                data-current={isCurrent ? "true" : "false"}
                onClick={() => onPick(row.id, label)}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "6px 12px",
                  textAlign: "left",
                  background: isCurrent ? "#37373a" : "transparent",
                  border: "none",
                  color: "inherit",
                  cursor: "pointer",
                  font: "inherit",
                }}
              >
                {label}
              </button>
            );
          })
        )}
        <div
          style={{
            borderTop: "1px solid #3a3a3e",
            marginTop: 4,
            paddingTop: 4,
          }}
        >
          <button
            type="button"
            data-testid="block-lookup-menu-cancel"
            onClick={onCancel}
            style={{
              display: "block",
              width: "100%",
              padding: "4px 12px",
              textAlign: "left",
              background: "transparent",
              border: "none",
              color: "#aaa",
              cursor: "pointer",
              font: "inherit",
            }}
          >
            {t("block.lookup.menu.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
